import {useCallback, useEffect, useMemo, useRef} from "react";
import {debounce} from "debounce";

type NonNullable<T> = Exclude<T, null | undefined>;

/**
 * returns debounced and immediate calls to fun(arg) with always the latest version of arg being used in the call.
 * naive debounce() calls within react might result in an old version of arg being used when the call happens.
 * @param interval interval in ms
 * @param fun the function to call
 * @param arg the arg to call the function with
 */
export function useDebounced<A, F extends (arg: NonNullable<A>) => void>(interval: number, fun: F, arg: A): [() => void, () => void] {
    const funRef = useRef<F>(fun);
    useEffect(() => {
        funRef.current = fun;
    }, [fun]);

    const argRef = useRef<A>(arg);
    useEffect(() => {
        argRef.current = arg;
    }, [arg]);

    const refFunctionCall = useCallback(() => {
        if (funRef.current != null && argRef.current != null) {
            funRef.current(argRef.current as NonNullable<A>);
        }
    }, []);

    const debouncedFunctionCall = useMemo(() => {
        return debounce(refFunctionCall, interval)
    }, [refFunctionCall, interval]);

    const immediateFunctionCall = () => {
        debouncedFunctionCall();
        debouncedFunctionCall.flush();
    };

    return [debouncedFunctionCall, immediateFunctionCall];
}